import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { ExternalLink, FileText, FlaskConical, PanelRightClose, PanelRightOpen, Stethoscope } from "lucide-react";
import { debounce } from "lodash";
import clsx from "clsx";
import toast from "react-hot-toast";
import { LAB_REPORT } from "@/api_framework/api_config";
import { IEMRUpdatePayloadProps } from "@/api_framework/api_modals/EMRs";
import {
  ILabReportInterpretationEMR,
  ILabReportInterpretationEMRDocuments,
  TLabReportInterpretationEMRStatus,
} from "@/api_framework/api_modals/LabReportInterpretation";
import LabTestDiagnosticsComponent from "@/app/components/routes/auth_routes/EMRTemplateCreationPage/LabTestDiagnosticsComponent";
import FollowUpComponent from "@/app/components/routes/auth_routes/EMRTemplateCreationPage/FollowUpComponent";
import MedicinesComponent from "@/app/components/routes/auth_routes/EMRTemplateCreationPage/MedicinesComponent";
import MedicinesSectionV2 from "@/app/components/routes/auth_routes/EMRTemplateCreationPage/MedicinesComponent/MedicinesSectionV2";
import PetProfileComponent from "@/app/components/routes/auth_routes/EMRTemplateCreationPage/PetProfileComponent";
import ReferToOtherClinicComponent from "@/app/components/routes/auth_routes/EMRTemplateCreationPage/ReferToOtherClinicComponent";
import { VMSEMRUpdateURLContext } from "@/app/context/VMSEMRUpdateURLContext";
import { useLabReportInterpretationEMRDetailAPI } from "@/app/hooks/api_hooks/LabReportInterpretation/useLabReportInterpretationEMRDetailAPI";
import { useLabReportInterpretationEMRUpdateAPI } from "@/app/hooks/api_hooks/LabReportInterpretation/useLabReportInterpretationEMRUpdateAPI";
import { useModal } from "@/app/hooks/ui_hooks/useModal";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useModalState } from "@/app/hooks/ui_hooks/useModalState";
import DocumentViewer, { IDocumentFile } from "./DocumentViewer";
import PreviousMedicationsSection from "./PreviousMedicationsSection";

interface ILabReportInterpretationEMRModalData {
  emrId: string;
  documents?: ILabReportInterpretationEMRDocuments;
}

interface ILabReportInterpretationEMRModalProps {
  onSaved: () => void;
}

const DOCUMENT_TAB_STYLES = {
  report: {
    label: "Lab Reports",
    Icon: FlaskConical,
    iconClass: "text-[#7C3AED]",
    badgeClass: "bg-[#F3E8FF] text-[#7C3AED]",
    pillActiveClass: "border-[#7C3AED] bg-[#F3E8FF] text-[#5B21B6]",
  },
  emr: {
    label: "EMRs",
    Icon: Stethoscope,
    iconClass: "text-[#1E3A8A]",
    badgeClass: "bg-[#E0E9FF] text-[#1E3A8A]",
    pillActiveClass: "border-[#1E3A8A] bg-[#EEF4FF] text-[#1E3A8A]",
  },
};

type TDocumentKind = keyof typeof DOCUMENT_TAB_STYLES;

interface IDocumentGroup {
  key: string;
  label: string;
  kind: TDocumentKind;
  files: IDocumentFile[];
}

const EMR_ID_PARAM = "emr_id";
const TEXT_FIELD_SAVE_DELAY_MS = 800;

const EMR_PANE_FIT_CLASS = String.raw`[&_.sm\:flex-nowrap]:!flex-wrap [&_table]:text-xs`;
const EMR_PANE_COMPACT_CLASS = "[&>*]:!mt-2 [&>*:first-child]:!mt-0 [&>*]:!p-2 lg:[&>*]:!p-2.5";
const EMR_PANE_HEADING_CLASS = "[&_h3]:!text-[#1E3A8A] [&_h3]:!font-semibold";
const EMR_PANE_HIDE_NOTES_CLASS = String.raw`[&_div:has(>textarea[name=instructions])]:hidden [&_#follow-up-section_div:has(>textarea)]:hidden [&_#refer-section_textarea]:hidden`;
const EMR_PANE_REFER_INLINE_CLASS = String.raw`[&_#refer-section]:flex [&_#refer-section]:flex-wrap [&_#refer-section]:items-center [&_#refer-section]:gap-x-4 [&_#refer-section>div]:flex [&_#refer-section>div]:flex-wrap [&_#refer-section>div]:!mt-0 [&_#refer-section>div]:border-0 [&_#refer-section>div>div]:!px-1 [&_#refer-section>div>div]:!py-0 [&_#refer-section>div>div]:!bg-transparent [&_#refer-section_label]:!w-auto [&_#refer-section_label]:!pt-0`;
const EMR_SKELETON_SECTION_HEIGHTS = ["h-12", "h-28", "h-12", "h-12", "h-16", "h-10"];
const SECTION_TITLE_CLASS = "text-sm px-2 mb-1.5";
const getPillClass = (isActive: boolean, kind: TDocumentKind) =>
  clsx(
    "inline-flex items-center gap-1.5 shrink-0 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-colors",
    isActive
      ? DOCUMENT_TAB_STYLES[kind].pillActiveClass
      : "border-gray-300 bg-white text-gray-800 hover:border-gray-400 hover:bg-gray-50",
  );
const TEXT_AREA_CLASS =
  "w-full p-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:ring-black focus:border-black outline-none resize-none disabled:bg-gray-50";

const LabReportInterpretationEMRModal = ({ onSaved }: ILabReportInterpretationEMRModalProps) => {
  const open = useModal("LAB_REPORT_INTERPRETATION_EMR_MODAL");
  const { setInvisible } = useModalActions("LAB_REPORT_INTERPRETATION_EMR_MODAL");
  const { emrId, documents } = useModalState(
    "LAB_REPORT_INTERPRETATION_EMR_MODAL",
  ) as ILabReportInterpretationEMRModalData;
  const [searchParams, setSearchParams] = useSearchParams();
  const { execute: fetchEMR, loading: emrLoading, error: emrError } = useLabReportInterpretationEMRDetailAPI();
  const { execute: updateEMR, loading: saving } = useLabReportInterpretationEMRUpdateAPI();
  const [emrDetails, setEmrDetails] = useState<ILabReportInterpretationEMR | null>(null);
  const [interpretation, setInterpretation] = useState("");
  const [medicalInstructions, setMedicalInstructions] = useState("");
  const [activeKind, setActiveKind] = useState<TDocumentKind | null>(null);
  const [activeGroupKey, setActiveGroupKey] = useState<string | null>(null);
  const [activeFileKey, setActiveFileKey] = useState<string | null>(null);
  const [showDocuments, setShowDocuments] = useState(true);

  const isEmrIdInURL = searchParams.get(EMR_ID_PARAM) === emrId;
  const isCompleted = emrDetails?.emr_status === "COMPLETED";

  const updateEmrDetailsState = (nextDetails: IEMRUpdatePayloadProps | null) => {
    if (!nextDetails) return;
    setEmrDetails((prev) => ({
      ...(prev ?? {}),
      ...nextDetails,
      pet_details: { ...(prev?.pet_details ?? {}), ...(nextDetails.pet_details ?? {}) },
      customer_details: { ...(prev?.customer_details ?? {}), ...(nextDetails.customer_details ?? {}) },
    }));
  };

  const loadEMR = async () => {
    const data = await fetchEMR(emrId);
    if (!data) return;
    updateEmrDetailsState(data);
    setInterpretation(data.interpretation ?? "");
    setMedicalInstructions(data.medical_instructions ?? "");
  };

  const setEmrIdParam = (value: string | null) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(EMR_ID_PARAM, value);
        else next.delete(EMR_ID_PARAM);
        return next;
      },
      { replace: true },
    );
  };

  useEffect(() => {
    if (!open || !emrId) return;
    setEmrDetails(null);
    setInterpretation("");
    setMedicalInstructions("");
    setActiveKind(null);
    setActiveGroupKey(null);
    setActiveFileKey(null);
    setEmrIdParam(emrId);
    loadEMR();
  }, [open, emrId]);

  const documentGroups = useMemo<IDocumentGroup[]>(() => {
    const linkedRequisitions = emrDetails?.linked_requisitions ?? documents?.linked_requisitions ?? [];
    const linkedEmrs = emrDetails?.linked_emrs ?? documents?.linked_emrs ?? [];
    const reportGroups: IDocumentGroup[] = linkedRequisitions.map((requisition) => ({
      key: `requisition-${requisition.requisition_id}`,
      label: requisition.test_name || requisition.requisition_number,
      kind: "report",
      files: (requisition.report_urls ?? []).map((url, index) => ({
        key: `requisition-${requisition.requisition_id}-${index}`,
        label: `File ${index + 1}`,
        url,
      })),
    }));
    const emrGroups: IDocumentGroup[] = linkedEmrs.map((linkedEmr) => ({
      key: `emr-${linkedEmr.emr_id}`,
      label: linkedEmr.emr_number,
      kind: "emr",
      files: linkedEmr.pdf_url ? [{ key: `emr-${linkedEmr.emr_id}-0`, label: "File 1", url: linkedEmr.pdf_url }] : [],
    }));
    return [...reportGroups, ...emrGroups];
  }, [documents, emrDetails?.linked_requisitions, emrDetails?.linked_emrs]);

  const documentCategories = (Object.keys(DOCUMENT_TAB_STYLES) as TDocumentKind[])
    .map((kind) => ({ kind, groups: documentGroups.filter((group) => group.kind === kind) }))
    .filter((category) => category.groups.length > 0);

  const activeCategory = documentCategories.find((category) => category.kind === activeKind) ?? documentCategories[0];
  const activeGroup = activeCategory?.groups.find((group) => group.key === activeGroupKey) ?? activeCategory?.groups[0];
  const activeFile = activeGroup?.files.find((file) => file.key === activeFileKey) ?? activeGroup?.files[0];

  const selectCategory = (kind: TDocumentKind) => {
    setActiveKind(kind);
    setActiveGroupKey(null);
    setActiveFileKey(null);
  };

  const selectGroup = (groupKey: string) => {
    setActiveGroupKey(groupKey);
    setActiveFileKey(null);
  };

  const saveTextFields = useMemo(
    () =>
      debounce((fields: { interpretation?: string; medical_instructions?: string }) => {
        updateEMR({ emr_id: emrId, ...fields });
      }, TEXT_FIELD_SAVE_DELAY_MS),
    [emrId],
  );

  useEffect(() => () => saveTextFields.cancel(), [saveTextFields]);

  const handleTextChange = (field: "interpretation" | "medical_instructions", value: string) => {
    if (field === "interpretation") setInterpretation(value);
    else setMedicalInstructions(value);
    saveTextFields({ [field]: value });
  };

  const handleClose = () => {
    if (saving) return;
    saveTextFields.flush();
    setEmrIdParam(null);
    setInvisible();
  };

  const handleSave = async (emrStatus: TLabReportInterpretationEMRStatus) => {
    saveTextFields.cancel();
    const updatedEMR = await updateEMR({
      emr_id: emrId,
      emr_status: emrStatus,
      interpretation,
      medical_instructions: medicalInstructions,
    });
    if (!updatedEMR) return;
    updateEmrDetailsState(updatedEMR);
    onSaved();
    if (emrStatus === "DRAFT") {
      toast.success("Draft saved");
      return;
    }
    toast.success("Interpretation submitted");
    setEmrIdParam(null);
    setInvisible();
  };

  const canSubmit = !saving && !emrLoading && interpretation.trim().length > 0;
  const shouldUseMedicinesV2 = emrDetails?.medicines?.sell_med_live === true;

  const renderEMRSections = () => {
    if (emrLoading && !emrDetails) {
      return (
        <div className="flex flex-col gap-2 motion-safe:animate-pulse" aria-busy="true" aria-label="Loading EMR">
          {EMR_SKELETON_SECTION_HEIGHTS.map((height, index) => (
            <div key={index} className="bg-white rounded-xl p-3">
              <div className="h-4 w-32 rounded bg-gray-200" />
              <div className={clsx("mt-3 rounded-lg bg-gray-100", height)} />
            </div>
          ))}
        </div>
      );
    }
    if (emrError && !emrDetails) return <p className="text-sm text-red-500 p-6">Unable to load EMR</p>;
    if (!emrDetails || !isEmrIdInURL) return null;
    return (
      <VMSEMRUpdateURLContext.Provider value={LAB_REPORT.LAB_REPORT_INTERPRETATION_EMR_UPDATE.baseURL}>
        <fieldset
          className={clsx(
            "min-w-0 flex-1 flex flex-col",
            EMR_PANE_FIT_CLASS,
            EMR_PANE_COMPACT_CLASS,
            EMR_PANE_HEADING_CLASS,
            EMR_PANE_HIDE_NOTES_CLASS,
            EMR_PANE_REFER_INLINE_CLASS,
          )}
        >
          <fieldset disabled className="contents [&>*]:!p-2 lg:[&>*]:!p-2.5">
            <PetProfileComponent emrDetails={emrDetails} hideTemperature />
          </fieldset>

          <div className="flex flex-col flex-1 bg-white rounded-xl p-3">
            <h3 className={SECTION_TITLE_CLASS}>
              Interpretation <span className="text-red-500">*</span>
            </h3>
            <textarea
              rows={4}
              value={interpretation}
              placeholder="Write your interpretation of the lab report..."
              onChange={(e) => handleTextChange("interpretation", e.target.value)}
              className={clsx(TEXT_AREA_CLASS, "flex-1")}
            />
          </div>

          <PreviousMedicationsSection emrId={emrId} emrDetails={emrDetails} setEmrDetails={updateEmrDetailsState} />

          {shouldUseMedicinesV2 ? (
            <MedicinesSectionV2 emrDetails={emrDetails} setEmrDetails={updateEmrDetailsState} dropdownZIndex={60} />
          ) : (
            <MedicinesComponent emrDetails={emrDetails} setEmrDetails={updateEmrDetailsState} dropdownZIndex={60} />
          )}

          <LabTestDiagnosticsComponent
            emrDetails={emrDetails}
            setEmrDetails={updateEmrDetailsState}
            recommendedTestsOnly
          />

          <div className="bg-white rounded-xl p-3">
            <h3 className={SECTION_TITLE_CLASS}>Medical Instruction</h3>
            <textarea
              rows={3}
              value={medicalInstructions}
              placeholder="Add medical instructions..."
              onChange={(e) => handleTextChange("medical_instructions", e.target.value)}
              className={TEXT_AREA_CLASS}
            />
          </div>

          <FollowUpComponent emrDetails={emrDetails} setEmrDetails={updateEmrDetailsState} isBilable />
          <ReferToOtherClinicComponent emrDetails={emrDetails} setEmrDetails={updateEmrDetailsState} />
        </fieldset>
      </VMSEMRUpdateURLContext.Provider>
    );
  };

  return (
    <Transition.Root show={!!open} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75" />
        </Transition.Child>

        <div className="fixed inset-0 flex justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel
              className={clsx(
                "relative flex flex-col w-full h-full bg-white rounded-2xl shadow-2xl overflow-hidden motion-safe:transition-[max-width] motion-safe:duration-300 ease-out",
                showDocuments ? "lg:max-w-[100vw]" : "lg:max-w-5xl",
              )}
            >
              <div className="flex items-center justify-between gap-4 px-5 py-3 border-b border-gray-200 bg-[#F9FAFB]">
                <div className="min-w-0">
                  <h2 className="text-base font-semibold text-gray-900 truncate">Lab Report Interpretation</h2>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => setShowDocuments((prev) => !prev)}
                    className="hidden lg:inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 bg-white text-xs font-medium text-gray-700 hover:bg-gray-100"
                  >
                    {showDocuments ? <PanelRightClose className="w-4 h-4" /> : <PanelRightOpen className="w-4 h-4" />}
                    {showDocuments ? "Hide Documents" : "Show Documents"}
                  </button>
                  <button
                    type="button"
                    aria-label="Close"
                    disabled={saving}
                    className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
                    onClick={handleClose}
                  >
                    <XMarkIcon className="h-5 w-5 text-gray-600" />
                  </button>
                </div>
              </div>

              <div className="flex flex-col lg:flex-row flex-1 min-h-0">
                <div
                  className={clsx(
                    "flex flex-col min-w-0 min-h-0 flex-1",
                    showDocuments && "lg:flex-none lg:w-[60%] lg:border-r border-gray-200",
                  )}
                >
                  <div className="flex-1 min-w-0 flex flex-col overflow-y-auto overflow-x-hidden overscroll-contain bg-[#F5F5F5] py-2 pl-3 pr-1.5">
                    {renderEMRSections()}
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-t border-gray-200 bg-white">
                    <p className="text-xs text-gray-500">
                      <span className="text-red-500">*</span> Interpretation is required to submit
                    </p>
                    <div className="flex items-center gap-3">
                      {!isCompleted && (
                        <button
                          type="button"
                          disabled={saving || emrLoading}
                          className="px-5 py-2.5 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                          onClick={() => handleSave("DRAFT")}
                        >
                          Save Draft
                        </button>
                      )}
                      <button
                        type="button"
                        disabled={!canSubmit}
                        className={clsx("px-6 py-2.5 text-xs font-semibold rounded-lg", {
                          "bg-gray-200 text-gray-400 cursor-not-allowed": !canSubmit,
                          "bg-black text-white hover:bg-gray-900": canSubmit,
                        })}
                        onClick={() => handleSave("COMPLETED")}
                      >
                        {saving ? "Saving..." : "Submit"}
                      </button>
                    </div>
                  </div>
                </div>

                <div className={clsx("hidden flex-col min-w-0 min-h-0 bg-white", showDocuments && "lg:flex lg:flex-1")}>
                  <div className="flex items-center gap-3 px-3 py-2 border-b border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-900 shrink-0">Documents</h3>
                    <div className="flex flex-1 min-w-0">
                      {documentCategories.length === 0 ? (
                        <p className="text-xs text-gray-500">No documents available</p>
                      ) : (
                        <div className="inline-flex gap-1 p-1 rounded-lg bg-gray-100 overflow-x-auto">
                          {documentCategories.map(({ kind, groups }) => {
                            const { label, Icon, iconClass, badgeClass } = DOCUMENT_TAB_STYLES[kind];
                            const isActive = activeCategory?.kind === kind;
                            return (
                              <button
                                key={kind}
                                type="button"
                                onClick={() => selectCategory(kind)}
                                className={clsx(
                                  "inline-flex items-center gap-2 shrink-0 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors",
                                  isActive ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-900",
                                )}
                              >
                                <Icon
                                  className={clsx("w-4 h-4", isActive ? iconClass : "text-gray-400")}
                                  aria-hidden="true"
                                />
                                {label}
                                <span
                                  className={clsx(
                                    "min-w-5 px-1.5 rounded-full text-[10px] leading-4 text-center",
                                    isActive ? badgeClass : "bg-gray-200 text-gray-600",
                                  )}
                                >
                                  {groups.length}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    {activeFile?.url && (
                      <a
                        href={activeFile.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 shrink-0 text-xs font-medium text-[#0467FF] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0467FF] rounded-sm"
                      >
                        Open in new tab <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
                      </a>
                    )}
                  </div>
                  {(activeCategory?.groups.length > 1 || activeGroup?.files.length > 1) && (
                    <div className="flex items-center gap-2 shrink-0 px-3 py-2 border-b border-gray-200 bg-white overflow-x-auto">
                      {activeCategory.groups.length > 1 &&
                        activeCategory.groups.map((group) => (
                          <button
                            key={group.key}
                            type="button"
                            onClick={() => selectGroup(group.key)}
                            className={getPillClass(activeGroup?.key === group.key, activeCategory.kind)}
                          >
                            <FileText
                              className={clsx("w-3.5 h-3.5", DOCUMENT_TAB_STYLES[activeCategory.kind].iconClass)}
                              aria-hidden="true"
                            />
                            <span title={group.label} className="max-w-40 truncate">
                              {group.label}
                            </span>
                          </button>
                        ))}
                      {activeCategory.groups.length > 1 && activeGroup?.files.length > 1 && (
                        <span className="shrink-0 h-4 w-px mx-1 bg-gray-300" aria-hidden="true" />
                      )}
                      {activeGroup?.files.length > 1 &&
                        activeGroup.files.map((file) => (
                          <button
                            key={file.key}
                            type="button"
                            onClick={() => setActiveFileKey(file.key)}
                            className={getPillClass(activeFile?.key === file.key, activeCategory.kind)}
                          >
                            {file.label}
                          </button>
                        ))}
                    </div>
                  )}
                  <div className="flex-1 min-h-0 bg-white">
                    <DocumentViewer document={activeFile} />
                  </div>
                </div>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default LabReportInterpretationEMRModal;
